// import { type Analytics, getAnalytics } from "firebase/analytics"
import { message } from "antd"
import { type Analytics, getAnalytics } from "firebase/analytics"
import {
  deleteApp,
  type FirebaseApp,
  getApp,
  getApps,
  initializeApp,
} from "firebase/app"
import { Auth, connectAuthEmulator, getAuth } from "firebase/auth"
import {
  arrayRemove,
  arrayUnion,
  clearIndexedDbPersistence,
  collection,
  connectFirestoreEmulator,
  deleteDoc,
  deleteField,
  doc,
  type DocumentData,
  type DocumentReference,
  type Firestore as FirestoreDb,
  getDoc,
  getDocs,
  getFirestore,
  initializeFirestore,
  limit,
  onSnapshot,
  orderBy,
  persistentLocalCache,
  persistentSingleTabManager,
  query,
  type QueryConstraint,
  runTransaction,
  setDoc,
  terminate,
  Transaction,
  type Unsubscribe,
  updateDoc,
  where,
  writeBatch,
} from "firebase/firestore"
import {
  connectStorageEmulator,
  FirebaseStorage,
  getStorage,
} from "firebase/storage"
import { chunk, cloneDeep, forEach, isEqual, keyBy, noop, uniq } from "lodash"

import { ATTENDANCE_STATUSES } from "#constants/globalConstants"
import { type ClassSessionConstructor } from "#customTypes/composedTypes/classSessionTypes"
import {
  type AddClassFormSubmitValues,
  type ClassConstructor,
} from "#customTypes/composedTypes/classTypes"
import {
  type AddCurriculumFormSubmitValues,
  type CurriculumConstructor,
} from "#customTypes/composedTypes/curriculumTypes"
import {
  EVAL_COMMENT_TYPE,
  type EvalCommentConstructor,
  type EvalEventType,
} from "#customTypes/composedTypes/evalCommentTypes"
import {
  COMPARATORS,
  DOC_TYPES,
  type FirebaseBatchUpdate,
  type FirebaseBatchUpdateResponse,
  FirebaseChange,
  type FirebaseQueryConfig,
} from "#customTypes/composedTypes/firebaseTypes"
import {
  type AddMakeupFormValues,
  type MakeupWriteValues,
} from "#customTypes/composedTypes/makeupTypes"
import { ProjectConstructor } from "#customTypes/composedTypes/projectTypes"
import { type QuickNotes } from "#customTypes/composedTypes/quickNotesTypes"
import { type StudentConstructor } from "#customTypes/composedTypes/studentTypes"
import { AddTryoutFormSubmitValues } from "#customTypes/composedTypes/tryoutTypes"
import { type UserConstructor } from "#customTypes/composedTypes/userTypes"
import { type Uuid } from "#customTypes/globalTypes"
import {
  type AttendanceStatus,
  type DatabaseObject,
  DataWithId,
  type GenericObject,
  invariant,
} from "#customTypes/types"
import { hashPin } from "#utils/authHelpers"
import { generateSessionId, getSessionInfoFromId } from "#utils/classHelpers"
import { getProjectScheduleBySession } from "#utils/curriculumHelpers"
import {
  familyRepeatInputFor,
  type FamilyRepeatMoment,
  familyRepeatWriteIsStale,
  type FamilyRepeatWriteResult,
} from "#utils/familyRepeatJudgment"
import { cleanFormData } from "#utils/formHelpers"
import { dayBeforeStart } from "#utils/semesterCalendar"
import {
  ATTENDED_PROJECT_STATUSES,
  getBlankAttendance,
} from "#utils/studentHelpers"
import { formatError, stringify } from "#utils/textFormatters"

import FirebaseAnalytics from "./FirebaseAnalytics"
import FirebaseAuth from "./FirebaseAuth"
import { default as Firestore } from "./Firestore"

const DEV_FIREBASE_LIMIT = undefined
// setLogLevel("debug")

// Firestore rejects "in" queries with more than 30 comparison values
const FIRESTORE_IN_QUERY_LIMIT = 30

// --- Firebase environment toggle (localhost only) ---

export type FirebaseEnv = "PROD" | "DEV"
const FIREBASE_ENV_KEY = "jarvis-firebase-env"
const CAN_TOGGLE_ENV = import.meta.env.VITE_DEBUG_MODE === "true"
export const USE_EMULATORS =
  import.meta.env.VITE_USE_FIREBASE_EMULATORS === "true"

// SYNC: fixtures/contract.ts EMULATOR_HOST / EMULATOR_PORTS
const EMULATOR_HOST = "127.0.0.1"
const EMULATOR_AUTH_PORT = 9099
const EMULATOR_FIRESTORE_PORT = 8080
const EMULATOR_STORAGE_PORT = 9199

const PROD_FIREBASE_CONFIG = {
  apiKey: "AIzaSyDorUyfa1EAY6WunedbOzSla6SqITgfc-8",
  authDomain: "firestore-test-d50d6.firebaseapp.com",
  projectId: "firestore-test-d50d6",
  storageBucket: "firestore-test-d50d6.firebasestorage.app",
  messagingSenderId: "129695111732",
  appId: "1:129695111732:web:1ac73089a9d5836dcec861",
  measurementId: "G-8GVGF7ZCJF",
}

const DEV_FIREBASE_CONFIG = {
  apiKey: "AIzaSyDNpN1Q_kI1_tCgp6Yb5CgDGONtgR18t5I",
  authDomain: "jarvis-demo-92f41.firebaseapp.com",
  projectId: "jarvis-demo-92f41",
  storageBucket: "jarvis-demo-92f41.firebasestorage.app",
  messagingSenderId: "1022631740341",
  appId: "1:1022631740341:web:15c75654477bf968d8cffc",
  measurementId: "G-R29LWBJ7CP",
}

// SYNC: fixtures/contract.ts EMULATOR_PROJECT_ID / EMULATOR_STORAGE_BUCKET.
// The Auth emulator never validates the api key, but the web SDK refuses to
// initialize without a non-empty one.
const EMULATOR_FIREBASE_CONFIG = {
  apiKey: "demo-emulator-api-key",
  authDomain: "localhost",
  projectId: "demo-jarvis",
  storageBucket: "demo-jarvis.appspot.com",
}

export const getFirebaseEnv = (): FirebaseEnv => {
  if (!CAN_TOGGLE_ENV) return "PROD"
  const stored = localStorage.getItem(FIREBASE_ENV_KEY)
  return stored === "PROD" ? "PROD" : "DEV"
}

export const setFirebaseEnv = (env: FirebaseEnv): void => {
  if (!CAN_TOGGLE_ENV) return
  localStorage.setItem(FIREBASE_ENV_KEY, env)
  window.location.reload()
}

export const withEnvParam = (url: string): string => {
  if (getFirebaseEnv() !== "DEV") return url
  const sep = url.includes("?") ? "&" : "?"
  return `${url}${sep}env=dev`
}

const firebaseConfig = USE_EMULATORS
  ? EMULATOR_FIREBASE_CONFIG
  : getFirebaseEnv() === "PROD"
    ? PROD_FIREBASE_CONFIG
    : DEV_FIREBASE_CONFIG

declare global {
  interface Window {
    /**
     * The Firebase project this bundle actually initialized with. The e2e
     * warmup tripwire reads it to prove the app under test is emulator-bound
     * before any spec runs — a non-emulator dev server adopted onto the test
     * port would otherwise fail every login against real Firebase rules.
     */
    __JARVIS_FIREBASE_PROJECT_ID__?: string
  }
}

if (typeof window !== "undefined") {
  window.__JARVIS_FIREBASE_PROJECT_ID__ = firebaseConfig.projectId
}

export const NULL_ID = "null-id"

const storedFamilyRepeatField = (
  data: unknown
): { key?: unknown; momentIds?: unknown } | null => {
  if (!data || typeof data !== "object") return null
  const field = (data as { familyRepeatJudgment?: unknown })
    .familyRepeatJudgment
  if (!field || typeof field !== "object") return null
  return field as { key?: unknown; momentIds?: unknown }
}

const storedFamilyRepeatKey = (data: unknown): string | null => {
  const key = storedFamilyRepeatField(data)?.key
  return typeof key === "string" && key.length > 0 ? key : null
}

/** Ids saved with the judgment. Missing or unreadable lists are not a set. */
const storedFamilyRepeatIds = (data: unknown): readonly string[] | null => {
  const ids = storedFamilyRepeatField(data)?.momentIds
  if (!Array.isArray(ids) || ids.length === 0) return null
  if (!ids.every((id) => typeof id === "string" && id.length > 0)) return null
  return ids
}

class FirebaseSingleton {
  private readonly db: FirestoreDb
  private readonly app: FirebaseApp
  public readonly storage: FirebaseStorage
  public readonly auth: Auth
  public readonly analytics: Analytics | null
  private _currentUserId: Uuid | null = null

  get currentUserId(): Uuid | null {
    return this._currentUserId
  }

  setCurrentUserId = (userId: Uuid | null) => {
    this._currentUserId = userId
  }

  /** ID token for the manager-login gate. Server /api/* routes require this
   * as a Bearer token; null when no one is signed into Firebase Auth. */
  getIdToken = async (): Promise<string | null> => {
    const user = this.auth.currentUser
    if (!user) return null
    return user.getIdToken()
  }

  private getUpdateMeta(): { lastUpdatedAt: string; lastUpdatedBy: Uuid } {
    invariant(
      this._currentUserId != null,
      "currentUserId is not set on Firebase instance!"
    )
    return {
      lastUpdatedAt: new Date().toISOString(),
      lastUpdatedBy: this._currentUserId,
    }
  }

  constructor() {
    this.app = getApps().length ? getApp() : initializeApp(firebaseConfig)

    if (USE_EMULATORS) {
      // No persistent cache: IndexedDB survives between Playwright tests and
      // makes them order-dependent.
      this.db = initializeFirestore(this.app, {})
    } else {
      // Race condition: multiple calls can trigger concurrent initializeFirestore.
      // Catch and fall back to getFirestore() if already initialized.
      try {
        this.db = initializeFirestore(this.app, {
          localCache: persistentLocalCache({
            tabManager: persistentSingleTabManager(),
          }),
        })
      } catch {
        this.db = getFirestore(this.app)
      }
    }

    this.storage = getStorage(this.app)
    this.auth = getAuth(this.app)
    // getAnalytics requires a real appId/measurementId and reaches for the
    // network; neither is available or wanted under the emulator.
    this.analytics = USE_EMULATORS ? null : getAnalytics(this.app)

    if (USE_EMULATORS) {
      connectAuthEmulator(
        this.auth,
        `http://${EMULATOR_HOST}:${EMULATOR_AUTH_PORT}`,
        { disableWarnings: true }
      )
      connectFirestoreEmulator(this.db, EMULATOR_HOST, EMULATOR_FIRESTORE_PORT)
      connectStorageEmulator(this.storage, EMULATOR_HOST, EMULATOR_STORAGE_PORT)
    }

    // Initialize other Firebase singletons
    new Firestore(this.storage)
    new FirebaseAuth(this.auth)
    new FirebaseAnalytics(this.analytics)
  }

  getNewRef = <T>(docType: DOC_TYPES, id?: Uuid): DocumentReference<T> => {
    return id
      ? (doc(this.db, docType, id) as DocumentReference<T>)
      : (doc(collection(this.db, docType)) as DocumentReference<T>)
  }

  addDocument = async <T extends DatabaseObject>(
    docType: DOC_TYPES,
    submittedData: Omit<T, "id"> & { id?: Uuid }
  ): Promise<Uuid> => {
    const cleanedValues = cleanFormData(submittedData) as T

    const docRef =
      cleanedValues.id == null
        ? this.getNewRef(docType)
        : doc(this.db, docType, cleanedValues.id)

    cleanedValues.id = docRef.id

    invariant(
      cleanedValues.id != null,
      `${docType} ID does not exist. Please provide an ID.`
    )

    await setDoc(
      docRef,
      Object.assign(cleanedValues, this.getUpdateMeta())
    ).catch((e) => {
      void message.error(stringify(e))
    })

    return docRef.id
  }

  updateDocument = async <T extends GenericObject>(
    docType: DOC_TYPES,
    id: Uuid,
    submittedData: T
  ): Promise<Uuid> => {
    const cleanedValues = cleanFormData(submittedData) as T
    const updateMeta = this.getUpdateMeta()
    const docRef = doc(this.db, docType, id)
    await updateDoc(docRef, Object.assign(cleanedValues, updateMeta)).catch(
      (e) => {
        void message.error(stringify(e))
      }
    )
    return docRef.id
  }

  /** Creates the doc when it is missing. Null fields are written, so a reschedule can clear sentAt. */
  mergeDocumentOrThrow = async <T extends GenericObject>(
    docType: DOC_TYPES,
    id: Uuid,
    submittedData: T
  ): Promise<Uuid> => {
    const cleanedValues = cleanFormData(submittedData) as T
    const docRef = doc(this.db, docType, id)
    await setDoc(docRef, Object.assign(cleanedValues, this.getUpdateMeta()), {
      merge: true,
    })
    return docRef.id
  }

  /** Same as updateDocument, but rejects instead of toasting so the caller owns the error UX. */
  updateDocumentOrThrow = async <T extends GenericObject>(
    docType: DOC_TYPES,
    id: Uuid,
    submittedData: T
  ): Promise<Uuid> => {
    const cleanedValues = cleanFormData(submittedData) as T
    const docRef = doc(this.db, docType, id)
    await updateDoc(docRef, Object.assign(cleanedValues, this.getUpdateMeta()))
    return docRef.id
  }

  /**
   * Stores a quick-story score only when the moment's note is still the
   * text that was scored. The read and the write are one transaction, so
   * an older score that finishes after a newer one does not replace it.
   * Returns false when the result is discarded.
   */
  writeQuickStoryScoreIfNoteMatches = async (
    momentId: Uuid,
    scoredNote: string,
    score: GenericObject
  ): Promise<boolean> => {
    const updateMeta = this.getUpdateMeta()
    const cleanedValues = cleanFormData({
      quickStoryScore: score,
    }) as GenericObject
    const ref = this.getRefById(DOC_TYPES.STANDOUT_MOMENTS, momentId)
    return this.runTransaction(async (transaction) => {
      const snap = await transaction.get(ref)
      if (!snap.exists()) return false
      const data = snap.data() as { note?: unknown }
      const currentNote = typeof data.note === "string" ? data.note.trim() : ""
      if (currentNote !== scoredNote) return false
      transaction.update(ref, Object.assign(cleanedValues, updateMeta))
      return true
    })
  }

  /**
   * Stores a family-repeat judgment when the student key is still empty,
   * still the key this job read, or a smaller moment set this job covers
   * after starting from nothing. Any other stored key is left alone. When
   * the stored key is already this job's key, nothing is written.
   *
   * Moment text is read before the transaction. The emulator locks every
   * document a transaction reads, and these moments are already watched
   * on the review page. Locking them together with the student deadlocks
   * that emulator: the portfolio student transaction never finishes, and
   * the users listener that login waits on never arrives.
   */
  writeFamilyRepeatJudgmentIfKeyMatches = async (
    studentId: Uuid,
    momentIds: readonly string[],
    expectedKey: string,
    judgment: GenericObject,
    observedKey: string | null
  ): Promise<FamilyRepeatWriteResult> => {
    const discarded = (): FamilyRepeatWriteResult => ({
      status: "stale",
      storedKey: null,
      storedIds: null,
    })
    const updateMeta = this.getUpdateMeta()
    const cleanedValues = cleanFormData({
      familyRepeatJudgment: judgment,
    }) as GenericObject
    const studentRef = this.getRefById(DOC_TYPES.STUDENTS, studentId)
    const momentRefs = momentIds.map((momentId) =>
      this.getRefById(DOC_TYPES.STANDOUT_MOMENTS, momentId)
    )
    const moments: FamilyRepeatMoment[] = []
    for (const ref of momentRefs) {
      const snap = await getDoc(ref)
      if (!snap.exists()) return discarded()
      const data = snap.data() as Record<string, unknown>
      const text = (value: unknown): string | null =>
        typeof value === "string" ? value : null
      const familyState = data.familyState
      moments.push({
        id: snap.id,
        studentId: typeof data.studentId === "string" ? data.studentId : "",
        category: text(data.category),
        dontShare: data.dontShare === true,
        familyState:
          familyState === "pending" ||
          familyState === "approved" ||
          familyState === "sent"
            ? familyState
            : null,
        isShared: data.isShared === true,
        note: text(data.note),
        generatedNote: text(data.generatedNote),
        familyNoteSource: text(data.familyNoteSource),
        createdAt: text(data.createdAt),
      })
    }
    // Same student's notes only. The ids were chosen for this student.
    // A moment that no longer belongs to them must not enter the key.
    if (moments.some((moment) => moment.studentId !== studentId)) {
      return discarded()
    }
    const fresh = familyRepeatInputFor(moments)
    if (fresh?.key !== expectedKey) return discarded()
    const expectedIds = fresh.momentIds
    return this.runTransaction(async (transaction) => {
      const studentSnap = await transaction.get(studentRef)
      if (!studentSnap.exists()) return discarded()
      const storedKey = storedFamilyRepeatKey(studentSnap.data())
      const storedIds = storedFamilyRepeatIds(studentSnap.data())
      if (storedKey === expectedKey) return { status: "current" }
      if (
        familyRepeatWriteIsStale(
          storedKey,
          expectedKey,
          observedKey,
          storedIds,
          expectedIds
        )
      ) {
        return { status: "stale", storedKey, storedIds }
      }
      transaction.update(studentRef, Object.assign(cleanedValues, updateMeta))
      return { status: "saved" }
    })
  }

  /** Drops the saved family-repeat judgment so the next load judges again. */
  clearFamilyRepeatJudgment = async (studentId: Uuid): Promise<void> => {
    const ref = this.getRefById(DOC_TYPES.STUDENTS, studentId)
    await updateDoc(ref, {
      familyRepeatJudgment: deleteField(),
      ...this.getUpdateMeta(),
    })
  }

  /** Update without setting lastUpdatedAt/lastUpdatedBy — for settings, preferences, etc. */
  updateDocumentSilent = async <T extends GenericObject>(
    docType: DOC_TYPES,
    id: Uuid,
    submittedData: T
  ): Promise<Uuid> => {
    const cleanedValues = cleanFormData(submittedData) as T
    const docRef = doc(this.db, docType, id)
    await updateDoc(docRef, cleanedValues).catch((e) => {
      void message.error(stringify(e))
    })
    return docRef.id
  }

  updateDocuments = async (
    submittedData: FirebaseBatchUpdate
  ): Promise<FirebaseBatchUpdateResponse> => {
    const batch = writeBatch(this.db)
    const updatedIds: Partial<{ [key in DOC_TYPES]: Uuid[] }> = {}
    const updateMeta = this.getUpdateMeta()
    // Process each document type separately
    forEach(submittedData, (docTypeData, docType) => {
      const docTypeEnum = docType as DOC_TYPES

      forEach(docTypeData, (itemData, id) => {
        const docRef = doc(this.db, docType, id)
        batch.update(docRef, Object.assign(itemData, updateMeta))

        // TS complains if this is outside the loop
        if (updatedIds[docTypeEnum] == null) {
          updatedIds[docTypeEnum] = []
        }

        updatedIds[docTypeEnum].push(id)
      })
    })

    await batch.commit()

    return updatedIds
  }

  getDocumentById = async <T extends DocumentData>(
    docType: DOC_TYPES,
    id: Uuid | undefined | null
  ): Promise<null | T> => {
    if (id == null) {
      return null
    }

    const docRef = doc(this.db, docType, id)
    const docSnap = await getDoc(docRef)

    if (!docSnap.exists()) {
      void message.error(`Data not found: ${docType} with ID ${id}`)
      return null
    }

    return docSnap.data() as T
  }

  getDocumentsByIds = async <T extends DocumentData & { id: Uuid }>(
    docType: DOC_TYPES,
    ids: Uuid[]
  ): Promise<T[]> => {
    if (ids.length === 0) {
      return []
    }

    // Firestore caps "in" queries at 30 values, so query in chunks
    const docSnapGroups = await Promise.all(
      chunk(uniq(ids), FIRESTORE_IN_QUERY_LIMIT).map((idChunk) =>
        getDocs(query(collection(this.db, docType), where("id", "in", idChunk)))
      )
    )

    // Firestore "in" queries don't preserve order - reorder to match input ids
    const docsById = keyBy(
      docSnapGroups.flatMap((docSnaps) =>
        docSnaps.docs.map((docSnap) => docSnap.data() as T)
      ),
      "id"
    )

    return ids.map((id) => docsById[id]).filter((doc): doc is T => doc != null)
  }

  getDocumentByRef = async <T extends DocumentData>(
    ref: DocumentReference<DocumentData>
  ): Promise<T | null> => {
    const docSnap = await getDoc(ref)
    if (docSnap.exists()) {
      return docSnap.data() as T
    }
    console.log(`🔥 No such document! ${ref.path}`)
    return null
  }

  getRefById = <T extends DocumentData>(
    docType: DOC_TYPES,
    id: Uuid
  ): DocumentReference<T> => {
    const docRef = doc(this.db, docType, id)
    return docRef as DocumentReference<T>
  }

  checkIfDocExists = async <T extends DocumentData>(
    docType: DOC_TYPES,
    id: Uuid | null | undefined
  ): Promise<boolean> => {
    if (!id) {
      return false
    }
    const docRef = this.getRefById<T>(docType, id)
    const docSnap = await getDoc(docRef)
    return docSnap.exists()
  }

  variableQuery = async <T extends DocumentData>(
    docType: DOC_TYPES,
    queryConfig: FirebaseQueryConfig
  ): Promise<T[]> => {
    const results: T[] = []
    const wheres = []

    for (const key in queryConfig.queries) {
      const { value, comparator = COMPARATORS.EQUALS } =
        queryConfig.queries[key]
      wheres.push(where(key, comparator, value))
    }

    const queryConstraints: QueryConstraint[] = [...wheres]

    if (queryConfig.orderBy != null) {
      queryConstraints.push(
        orderBy(queryConfig.orderBy.field, queryConfig.orderBy.direction)
      )
    }

    if (queryConfig.limitCount != null) {
      queryConstraints.push(limit(queryConfig.limitCount))
    }

    const q = query(collection(this.db, docType), ...queryConstraints)
    const querySnapshot = await getDocs(q)

    querySnapshot.forEach((doc) => {
      results.push(doc.data() as T)
    })

    return results
  }

  deleteDocument = async (docType: DOC_TYPES, id: Uuid): Promise<void> => {
    await deleteDoc(doc(this.db, docType, id))
  }

  /** Delete all documents in a collection where `field` equals `value` (batched). */
  deleteDocumentsByFieldEquals = async (
    docType: DOC_TYPES,
    field: string,
    value: string
  ): Promise<number> => {
    const q = query(collection(this.db, docType), where(field, "==", value))
    const snapshot = await getDocs(q)
    const docs = snapshot.docs
    const chunkSize = 400
    for (let i = 0; i < docs.length; i += chunkSize) {
      const batch = writeBatch(this.db)
      for (const d of docs.slice(i, i + chunkSize)) {
        batch.delete(d.ref)
      }
      await batch.commit()
    }
    return docs.length
  }

  runTransaction = async <T>(
    callback: (transaction: Transaction) => Promise<T>
  ): Promise<T> => {
    return runTransaction(this.db, callback)
  }

  getAllDocsOfType = async <T extends DatabaseObject>(
    docType: DOC_TYPES
  ): Promise<T[]> => {
    const results: T[] = []
    const querySnapshot = await getDocs(collection(this.db, docType))

    querySnapshot.forEach((docSnap) => {
      if (docSnap.exists()) {
        results.push(docSnap.data() as T)
      }
    })

    return results
  }

  createOrUpdateClassWithSessions = async ({
    classObj,
    sessions,
  }: AddClassFormSubmitValues): Promise<{
    classId: Uuid
    sessionIds: Uuid[]
  }> => {
    const isNew = classObj.id == null
    const updateMeta = this.getUpdateMeta()
    try {
      const result = await this.runTransaction<{
        classId: Uuid
        sessionIds: Uuid[]
      }>(async (transaction) => {
        let classRef: DocumentReference<ClassConstructor>

        const curriculumId = classObj.curriculumId as Uuid | null

        const curriculum = await this.getDocumentById<CurriculumConstructor>(
          DOC_TYPES.CURRICULUMS,
          curriculumId
        )

        const projectIds = curriculum?.projectIds ?? []

        const projects = await this.getDocumentsByIds<ProjectConstructor>(
          DOC_TYPES.PROJECTS,
          projectIds
        )

        if (projectIds.length === 0) {
          throw new Error(
            `${curriculum?.name ?? "Selected curriculum"} has no projects. Add projects before saving a class with this curriculum.`
          )
        }

        const projectSchedules = getProjectScheduleBySession(projects)

        if (isNew) {
          classRef = this.getNewRef<ClassConstructor>(DOC_TYPES.CLASSES)
          classObj.id = classRef.id
          const sessionIds: Uuid[] = []

          sessions.forEach((session, i) => {
            const sessionRef = this.getNewRef<ClassSessionConstructor>(
              DOC_TYPES.CLASS_SESSIONS,
              generateSessionId(classRef.id, i)
            )

            const projectSchedule = projectSchedules[i]
            invariant(
              projectSchedule != null,
              `Project schedule is missing for session ${i}. Check that the selected curriculum has enough project content for every session.`
            )

            session.projectId = projectSchedule.project.id
            session.projectFraction = projectSchedule.fraction

            session.id = sessionRef.id
            session.classId = classRef.id
            sessionIds.push(session.id)

            transaction.set(sessionRef, Object.assign(session, updateMeta))
          })

          classObj.sessionIds = sessionIds
          transaction.set(classRef, Object.assign(classObj, updateMeta))
        } else {
          invariant(classObj.id != null, "This class is missing an ID!")

          classRef = this.getRefById<ClassConstructor>(
            DOC_TYPES.CLASSES,
            classObj.id
          )

          const sessionRefs = sessions.map((session, i) => {
            invariant(session.id != null, `Session ${i} is missing an ID!`)

            return this.getRefById<ClassSessionConstructor>(
              DOC_TYPES.CLASS_SESSIONS,
              session.id
            )
          })

          // Rebuild the master rosters from the per-session rosters, which are
          // the source of truth. This also repairs docs whose membership
          // arrays drifted while the stale-form-snapshot clobber bug was live.
          // Transaction reads must all happen before any writes.
          const sessionSnaps = await Promise.all(
            sessionRefs.map((ref) => transaction.get(ref))
          )
          const sessionDocs = sessionSnaps.map((snap) => snap.data())
          classObj.studentIds = uniq(
            sessionDocs.flatMap((session) => session?.studentIds ?? [])
          )
          classObj.teacherIds = uniq(
            sessionDocs.flatMap((session) => session?.teacherIds ?? [])
          )
          classObj.assistantIds = uniq(
            sessionDocs.flatMap((session) => session?.assistantIds ?? [])
          )

          sessions.forEach((session, i) => {
            const sessionRef = sessionRefs[i]
            const projectSchedule = projectSchedules[i]
            invariant(
              projectSchedule != null,
              `Project schedule is missing for session ${i}. Check that the selected curriculum has enough project content for every session.`
            )

            session.projectId = projectSchedule.project.id
            session.projectFraction = projectSchedule.fraction

            transaction.update(sessionRef, Object.assign(session, updateMeta))
          })

          transaction.update(classRef, Object.assign(classObj, updateMeta))
        }

        return {
          classId: classRef.id,
          // Casting because ts isn't smart enough to infer the type
          sessionIds: sessions.map(({ id }) => id) as Uuid[],
        }
      })

      return result

      // TODO: Add error handling
    } catch (error) {
      console.error("Transaction failed:", error)
      FirebaseAnalytics.getInstance().trackError(
        error as Error,
        "class_creation"
      )
      // alert(`Transaction failed: ${error}`)
      throw new Error(formatError(error as Error))
      // return { classId: "", sessionIds: [] }
    }
  }

  // Should send through updated student attendance as well
  createOrUpdateMakeup = async (
    values: MakeupWriteValues,
    initialValues: AddMakeupFormValues
  ): Promise<Uuid | undefined> => {
    const isNew = values.id == null
    let makeupId: Uuid | undefined = values.id
    const {
      isMakeupScheduled,
      makeupSessionId,
      missedClassId,
      missedSessionId,
      studentId,
    } = values

    const {
      missedClassId: initialMissedClassId,
      missedSessionId: initialMissedSessionId,
      isMakeupScheduled: initialIsMakeupScheduled,
      makeupSessionId: initialMakeupSessionId,
    } = initialValues

    const updateMeta = this.getUpdateMeta()
    const needsAttendanceUpdate =
      isMakeupScheduled !== initialIsMakeupScheduled ||
      isNew ||
      missedSessionId !== initialMissedSessionId

    await this.runTransaction<void>(async (transaction) => {
      // ── READS (must come before all writes) ──

      let studentData: StudentConstructor | null = null
      let missedClassData: ClassConstructor | null = null

      if (needsAttendanceUpdate) {
        const studentRef = this.getRefById<StudentConstructor>(
          DOC_TYPES.STUDENTS,
          studentId
        )
        const missedClassRef = this.getRefById(DOC_TYPES.CLASSES, missedClassId)

        const [studentSnap, missedClassSnap] = await Promise.all([
          transaction.get(studentRef),
          transaction.get(missedClassRef),
        ])

        invariant(
          studentSnap.exists(),
          `Student ${studentId} not found when submitting makeup!`
        )
        invariant(
          missedClassSnap.exists(),
          `Missed class ${missedClassId} not found when submitting makeup!`
        )

        studentData = studentSnap.data() as StudentConstructor
        missedClassData = missedClassSnap.data() as ClassConstructor
      }

      // ── WRITES ──

      // Create or update makeup. The payload is not a full MakeupConstructor
      // on create (id is allocated here; some fields are omitted).
      const storedMakeup: DocumentData = {}
      Object.assign(storedMakeup, values, updateMeta)
      if (isNew) {
        const makeupRef = this.getNewRef<DocumentData>(DOC_TYPES.MAKEUPS)
        storedMakeup.id = makeupRef.id
        values.id = makeupRef.id
        makeupId = makeupRef.id

        transaction.set(makeupRef, storedMakeup)
      } else {
        invariant(values.id != null, "This makeup is missing an ID!")
        storedMakeup.id = values.id
        const makeupRef = this.getRefById<DocumentData>(
          DOC_TYPES.MAKEUPS,
          values.id
        )

        transaction.update(makeupRef, storedMakeup)
      }

      // STUDENT ATTENDANCE
      if (needsAttendanceUpdate && studentData && missedClassData) {
        // ?? instead of a destructuring default: the field can be an explicit null
        const attendance = studentData.attendance ?? {}

        const sessionIndex: number = missedClassData.sessionIds.findIndex(
          (sessionId: Uuid) => sessionId === missedSessionId
        )

        invariant(
          sessionIndex != null && sessionIndex >= 0,
          "Session index not found"
        )

        const newAttendance = cloneDeep(attendance)

        newAttendance[missedClassId][sessionIndex] = {
          status: isMakeupScheduled
            ? ATTENDANCE_STATUSES.MAKEUP_ENROLLED
            : ATTENDANCE_STATUSES.PLANNED_ABSENCE,
          sessionId: missedSessionId,
        }

        if (
          initialMissedSessionId != null &&
          initialMissedClassId != null &&
          initialMissedSessionId !== missedSessionId
        ) {
          const oldSessionIndex = missedClassData.sessionIds.findIndex(
            (sessionId: Uuid) => sessionId === initialMissedSessionId
          )

          newAttendance[initialMissedClassId][oldSessionIndex] = {
            status: ATTENDANCE_STATUSES.ENROLLED,
            sessionId: initialMissedSessionId,
          }
        }

        const studentRef = this.getRefById<StudentConstructor>(
          DOC_TYPES.STUDENTS,
          studentId
        )
        transaction.update(
          studentRef,
          Object.assign({ attendance: newAttendance }, updateMeta)
        )
      }

      // MISSED SESSION
      if (isNew || initialMissedSessionId !== missedSessionId) {
        const missedSessionRef = this.getRefById<ClassSessionConstructor>(
          DOC_TYPES.CLASS_SESSIONS,
          values.missedSessionId
        )

        transaction.update(
          missedSessionRef,
          Object.assign(
            {
              studentIds: arrayRemove(values.studentId),
              plannedAbsenceIds: arrayUnion(values.id),
            },
            updateMeta
          )
        )
      }

      // If missed session changed, restore old session
      if (
        missedSessionId !== initialMissedSessionId &&
        initialMissedSessionId != null
      ) {
        const previousMissedSessionRef =
          this.getRefById<ClassSessionConstructor>(
            DOC_TYPES.CLASS_SESSIONS,
            initialMissedSessionId
          )

        transaction.update(
          previousMissedSessionRef,
          Object.assign(
            {
              studentIds: arrayUnion(studentId),
              plannedAbsenceIds: arrayRemove(values.id),
            },
            updateMeta
          )
        )
      }

      // MAKEUP SESSION
      if (isMakeupScheduled && makeupSessionId != null) {
        const makeupSessionRef = this.getRefById<ClassSessionConstructor>(
          DOC_TYPES.CLASS_SESSIONS,
          makeupSessionId
        )

        transaction.update(
          makeupSessionRef,
          Object.assign({ makeupIds: arrayUnion(values.id) }, updateMeta)
        )
      }

      // If makeup session changed, remove from old session
      if (
        makeupSessionId !== initialMakeupSessionId &&
        initialMakeupSessionId != null
      ) {
        const previousMakeupSessionRef =
          this.getRefById<ClassSessionConstructor>(
            DOC_TYPES.CLASS_SESSIONS,
            initialMakeupSessionId
          )

        transaction.update(
          previousMakeupSessionRef,
          Object.assign({ makeupIds: arrayRemove(values.id) }, updateMeta)
        )
      }
    })

    return makeupId
  }

  /**
   * Flips one task's completion inside a student's `tasks[]`.
   *
   * A transaction rather than a read-then-write from a subscription snapshot:
   * `tasks` is a single array field, so writing it back wholesale from a stale
   * client copy loses any sibling task completed in between. Front desk staff
   * check several tasks off in a row (the roster line's popover puts them one
   * click apart), which is faster than the Firestore listener round trip.
   */
  toggleStudentTask = async ({
    studentId,
    taskId,
    isCompleted,
    completedBy,
  }: {
    studentId: Uuid
    taskId: string
    isCompleted: boolean
    completedBy: Uuid
  }): Promise<void> => {
    const updateMeta = this.getUpdateMeta()

    await this.runTransaction<void>(async (transaction) => {
      const studentRef = this.getRefById<StudentConstructor>(
        DOC_TYPES.STUDENTS,
        studentId
      )
      const studentSnap = await transaction.get(studentRef)

      invariant(
        studentSnap.exists(),
        `Student ${studentId} not found when toggling a task!`
      )

      const student = studentSnap.data() as StudentConstructor
      // ?? instead of a destructuring default: the field can be an explicit null
      const tasks = student.tasks ?? []
      const taskIndex = tasks.findIndex((task) => task.id === taskId)

      invariant(
        taskIndex >= 0,
        `Task ${taskId} not found on student ${studentId} when toggling it!`
      )

      const updatedTasks = [...tasks]
      updatedTasks[taskIndex] = {
        ...tasks[taskIndex],
        isCompleted,
        completedAt: isCompleted ? new Date().toISOString() : null,
        completedBy: isCompleted ? completedBy : null,
      }

      transaction.update(
        studentRef,
        Object.assign({ tasks: updatedTasks }, updateMeta)
      )
    })
  }

  /**
   * Create-or-update in one transaction. A separate existence check followed
   * by `setDoc` would let a doc created in between be overwritten wholesale.
   * Without an id there is nothing to race on, so it creates directly.
   */
  upsertDocument = async <T extends DatabaseObject>(
    docType: DOC_TYPES,
    submittedData: Partial<T> & { id?: Uuid }
  ): Promise<Uuid> => {
    const cleanedValues = cleanFormData(submittedData) as Partial<T>
    const updateMeta = this.getUpdateMeta()
    const createMeta = {
      createdAt: updateMeta.lastUpdatedAt,
      createdBy: updateMeta.lastUpdatedBy,
    }

    if (cleanedValues.id == null) {
      const docRef = this.getNewRef<T>(docType)
      await setDoc(
        docRef,
        Object.assign(cleanedValues, createMeta, updateMeta, { id: docRef.id })
      )
      return docRef.id
    }

    const docRef = this.getRefById<T>(docType, cleanedValues.id)
    await this.runTransaction<void>(async (transaction) => {
      const snap = await transaction.get(docRef)
      if (snap.exists()) {
        transaction.update(docRef, Object.assign(cleanedValues, updateMeta))
      } else {
        transaction.set(
          docRef,
          Object.assign(cleanedValues, createMeta, updateMeta) as T
        )
      }
    })
    return docRef.id
  }

  /**
   * Sets one session's status in a person's attendance map.
   *
   * `attendance.{classId}` is an array, and Firestore cannot address an array
   * element by path, so the class's array has to be rewritten. Reading it
   * inside the transaction (not from a subscription snapshot) keeps a
   * concurrent edit to another session of the same class from being lost.
   * For student `attendance`, the attended-project counter is adjusted from
   * the same fresh read.
   */
  setAttendanceStatus = async ({
    docType,
    id,
    attendanceField,
    classId,
    sessionIndex,
    status,
    projectId,
  }: {
    docType: DOC_TYPES.STUDENTS | DOC_TYPES.USERS
    id: Uuid
    attendanceField: "attendance" | "assistantAttendance" | "teacherAttendance"
    classId: Uuid
    sessionIndex: number
    status: AttendanceStatus
    projectId?: Uuid
  }): Promise<void> => {
    const updateMeta = this.getUpdateMeta()

    await this.runTransaction<void>(async (transaction) => {
      const ref = this.getRefById<StudentConstructor | UserConstructor>(
        docType,
        id
      )
      const snap = await transaction.get(ref)

      invariant(
        snap.exists(),
        `Data for _${docType}_id ${id} is missing when setting attendance!`
      )

      const data = snap.data() as Partial<StudentConstructor & UserConstructor>
      const classAttendance = data[attendanceField]?.[classId]
      const existingSession = classAttendance?.[sessionIndex]

      invariant(
        classAttendance != null && existingSession != null,
        `Class session ${classId}-${sessionIndex} not found in ${attendanceField} for _${docType}_id ${id}!`
      )

      const nextClassAttendance = [...classAttendance]
      nextClassAttendance[sessionIndex] = { ...existingSession, status }

      const patch: Record<string, unknown> = {
        [`${attendanceField}.${classId}`]: nextClassAttendance,
      }

      if (attendanceField === "attendance" && projectId) {
        const delta =
          Number(ATTENDED_PROJECT_STATUSES.has(status)) -
          Number(ATTENDED_PROJECT_STATUSES.has(existingSession.status))

        if (delta !== 0) {
          const currentCount = data.projects?.[projectId] ?? 0
          patch[`projects.${projectId}`] = Math.max(0, currentCount + delta)
        }
      }

      transaction.update(ref, Object.assign(patch, updateMeta))
    })
  }

  /**
   * Deletes a makeup and undoes its effects on the student and sessions.
   *
   * The student's attendance for the missed class is read inside the
   * transaction and only the missed session's entry changes. Firestore
   * transactions cannot run queries, so the caller finds any other makeups on
   * the same missed-session slot beforehand; they are re-read here so one
   * deleted in the meantime is not counted.
   */
  deleteMakeup = async ({
    makeupId,
    studentId,
    missedSessionId,
    makeupSessionId,
    sameSlotMakeupIds,
  }: {
    makeupId: Uuid
    studentId: Uuid
    missedSessionId: Uuid
    makeupSessionId?: Uuid | null
    sameSlotMakeupIds: Uuid[]
  }): Promise<void> => {
    const updateMeta = this.getUpdateMeta()
    const [classId, sessionIndex] = getSessionInfoFromId(missedSessionId)

    await this.runTransaction<void>(async (transaction) => {
      // ── READS ──
      const studentRef = this.getRefById<StudentConstructor>(
        DOC_TYPES.STUDENTS,
        studentId
      )
      const otherMakeupRefs = sameSlotMakeupIds
        .filter((id) => id !== makeupId)
        .map((id) => this.getRefById<MakeupConstructor>(DOC_TYPES.MAKEUPS, id))

      const [studentSnap, ...otherMakeupSnaps] = await Promise.all([
        transaction.get(studentRef),
        ...otherMakeupRefs.map((ref) => transaction.get(ref)),
      ])

      invariant(
        studentSnap.exists(),
        `Data for _student_id ${studentId} is missing when deleting makeup ${makeupId}!`
      )

      const student = studentSnap.data() as StudentConstructor
      const classAttendance = student.attendance?.[classId]
      const existingSession = classAttendance?.[sessionIndex]

      invariant(
        classAttendance != null && existingSession != null,
        `Attendance for class ${classId} session ${sessionIndex} not found for _student_id ${studentId}!`
      )

      const remainingMakeups = otherMakeupSnaps
        .filter((snap) => snap.exists())
        .map((snap) => snap.data() as MakeupConstructor)

      // Another makeup still claiming this slot keeps it absent rather than
      // reverting to ENROLLED, which would orphan the remaining makeup.
      let nextStatus: AttendanceStatus = ATTENDANCE_STATUSES.ENROLLED
      if (remainingMakeups.length > 0) {
        nextStatus = remainingMakeups.some((m) => m.isMakeupScheduled)
          ? ATTENDANCE_STATUSES.MAKEUP_ENROLLED
          : ATTENDANCE_STATUSES.PLANNED_ABSENCE
      }

      const nextClassAttendance = [...classAttendance]
      nextClassAttendance[sessionIndex] = {
        ...existingSession,
        status: nextStatus,
      }

      // ── WRITES ──
      transaction.delete(
        this.getRefById<MakeupConstructor>(DOC_TYPES.MAKEUPS, makeupId)
      )

      const missedSessionUpdate: Record<string, unknown> = {
        plannedAbsenceIds: arrayRemove(makeupId),
      }
      if (remainingMakeups.length === 0) {
        missedSessionUpdate.studentIds = arrayUnion(studentId)
      }
      transaction.update(
        this.getRefById<ClassSessionConstructor>(
          DOC_TYPES.CLASS_SESSIONS,
          missedSessionId
        ),
        Object.assign(missedSessionUpdate, updateMeta)
      )

      transaction.update(
        studentRef,
        Object.assign(
          { [`attendance.${classId}`]: nextClassAttendance },
          updateMeta
        )
      )

      if (makeupSessionId) {
        transaction.update(
          this.getRefById<ClassSessionConstructor>(
            DOC_TYPES.CLASS_SESSIONS,
            makeupSessionId
          ),
          Object.assign({ makeupIds: arrayRemove(makeupId) }, updateMeta)
        )
      }
    })
  }

  /**
   * Adds a personal note, or patches one by id. `personalNotes` is an array,
   * so edits rewrite it; the transaction re-reads it so a note added or
   * changed in another tab is not dropped. New ids come from the fresh length.
   */
  addPersonalNote = async (
    userId: Uuid,
    description: string
  ): Promise<void> => {
    const updateMeta = this.getUpdateMeta()

    await this.runTransaction<void>(async (transaction) => {
      const userRef = this.getRefById<UserConstructor>(DOC_TYPES.USERS, userId)
      const userSnap = await transaction.get(userRef)

      invariant(
        userSnap.exists(),
        `Data for _user_id ${userId} is missing when adding a note!`
      )

      const notes = (userSnap.data() as UserConstructor).personalNotes ?? []

      transaction.update(
        userRef,
        Object.assign(
          {
            personalNotes: arrayUnion({
              id: notes.length,
              description,
              createdAt: updateMeta.lastUpdatedAt,
              createdBy: userId,
            }),
          },
          updateMeta
        )
      )
    })
  }

  updatePersonalNote = async (
    userId: Uuid,
    noteId: QuickNotes["id"],
    patch: Partial<
      Pick<
        QuickNotes,
        "completedAt" | "completedBy" | "deletedAt" | "deletedBy"
      >
    >
  ): Promise<void> => {
    const updateMeta = this.getUpdateMeta()

    await this.runTransaction<void>(async (transaction) => {
      const userRef = this.getRefById<UserConstructor>(DOC_TYPES.USERS, userId)
      const userSnap = await transaction.get(userRef)

      invariant(
        userSnap.exists(),
        `Data for _user_id ${userId} is missing when updating a note!`
      )

      const notes = (userSnap.data() as UserConstructor).personalNotes ?? []
      const noteIndex = notes.findIndex((note) => note.id === noteId)

      invariant(
        noteIndex >= 0,
        `Note ${noteId} not found for _user_id ${userId}!`
      )

      const nextNotes = [...notes]
      nextNotes[noteIndex] = { ...notes[noteIndex], ...patch }

      transaction.update(
        userRef,
        Object.assign({ personalNotes: nextNotes }, updateMeta)
      )
    })
  }

  /**
   * Updates an evaluation and records its history event in one batch, so an
   * evaluation can't change status without the matching eval-history entry.
   * Throws on failure instead of toasting, so callers can surface the error.
   */
  updateEvaluationWithEvent = async ({
    evaluationId,
    patch,
    event,
  }: {
    evaluationId: Uuid
    patch: GenericObject
    event?: { authorId: Uuid; eventType: EvalEventType; message?: string }
  }): Promise<void> => {
    const updateMeta = this.getUpdateMeta()
    const batch = writeBatch(this.db)

    batch.update(
      this.getRefById(DOC_TYPES.EVALUATIONS, evaluationId),
      Object.assign(cleanFormData(patch) as GenericObject, updateMeta)
    )

    if (event) {
      const eventRef = this.getNewRef<DocumentData>(DOC_TYPES.EVAL_COMMENTS)
      const eventDoc: EvalCommentConstructor = {
        id: eventRef.id,
        evaluationId,
        authorId: event.authorId,
        message: event.message ?? "",
        type: EVAL_COMMENT_TYPE.EVENT,
        eventType: event.eventType,
        createdAt: updateMeta.lastUpdatedAt,
        createdBy: event.authorId,
        lastUpdatedAt: updateMeta.lastUpdatedAt,
        lastUpdatedBy: event.authorId,
      }
      batch.set(eventRef, eventDoc)
    }

    await batch.commit()
  }

  // The add-tryout form writes identity + step0. Progress steps are filled
  // later. getUpdateMeta stamps lastUpdatedAt/lastUpdatedBy on the write.
  createTryout = async (values: AddTryoutFormSubmitValues): Promise<void> => {
    const { studentId, sessionId } = values
    const updateMeta = this.getUpdateMeta()
    const [classId, sessionIndex] = getSessionInfoFromId(sessionId)

    await this.runTransaction(async (transaction) => {
      // ── READS ──
      const studentRef = this.getRefById<StudentConstructor>(
        DOC_TYPES.STUDENTS,
        studentId
      )
      const classRef = this.getRefById<ClassConstructor>(
        DOC_TYPES.CLASSES,
        classId
      )

      const [studentSnap, classSnap] = await Promise.all([
        transaction.get(studentRef),
        transaction.get(classRef),
      ])

      invariant(
        studentSnap.exists(),
        `Student ${values.studentId} not found when submitting tryout!`
      )
      const student = studentSnap.data() as StudentConstructor
      // ?? instead of a destructuring default: the field can be an explicit null
      const attendance = student.attendance ?? {}

      if (!attendance[classId]) {
        invariant(
          classSnap.exists(),
          `Class ${classId} not found when submitting tryout!`
        )
        const classObj = classSnap.data() as ClassConstructor
        attendance[classId] = getBlankAttendance(classObj.sessionIds)
      }

      attendance[classId][sessionIndex].status =
        ATTENDANCE_STATUSES.TRYOUT_ENROLLED

      // ── WRITES ──
      // The create payload is identity + step0, not a full TryoutConstructor.
      // Progress steps are written later by the progress form.
      const tryoutRef = this.getNewRef<DocumentData>(DOC_TYPES.TRYOUTS)
      values.id = tryoutRef.id
      const storedTryout: DocumentData = {}
      Object.assign(storedTryout, values, updateMeta)
      transaction.set(tryoutRef, storedTryout)

      const classSessionRef = this.getRefById<ClassSessionConstructor>(
        DOC_TYPES.CLASS_SESSIONS,
        sessionId
      )
      transaction.update(
        classSessionRef,
        Object.assign({ tryoutIds: arrayUnion(values.id) }, updateMeta)
      )

      transaction.update(studentRef, Object.assign({ attendance }, updateMeta))
    })
  }

  // updateTryoutProgress = async (
  //   values: TryoutProgressSubmitData
  // ): Promise<void> => {
  //   await runTransaction(this.db, async (transaction) => {
  //     const { student, tryoutClassSession, tryout } = values

  //     // Update student
  //     const studentRef = this.getRefById<StudentConstructor>(
  //       DOC_TYPES.STUDENTS,
  //       student.id
  //     )
  //     transaction.update(studentRef, student)

  //     const tryoutRef = this.getRefById<TryoutConstructor>(
  //       DOC_TYPES.TRYOUTS,
  //       tryout.id
  //     )

  //     transaction.update(tryoutRef, tryout)

  //     const tryoutClassSessionRef = this.getRefById<ClassSessionConstructor>(
  //       DOC_TYPES.CLASS_SESSIONS,
  //       tryoutClassSession.id
  //     )

  //     transaction.update(tryoutClassSessionRef, tryoutClassSession)
  //   })
  // }

  createOrUpdateCurriculum = async (
    values: AddCurriculumFormSubmitValues,
    initialValues: AddCurriculumFormSubmitValues | undefined
  ): Promise<Uuid | undefined> => {
    const isNew = values.id == null
    let curriculumId: Uuid | undefined = values.id
    const updateMeta = this.getUpdateMeta()

    await runTransaction(this.db, async (transaction) => {
      const projectIds = values.projectIds || []
      const initialProjectIds = initialValues?.projectIds || []

      // If projects or their order changed, need to update class sessions
      if (!isEqual(projectIds, initialProjectIds)) {
        const sessionIndexesToUpdate: number[] = []
        const numberOfSessions = values.numberOfSessions ?? 0

        // Get all projects for this curriculum
        const projectsPromise = this.getDocumentsByIds<ProjectConstructor>(
          DOC_TYPES.PROJECTS,
          uniq([...projectIds, ...initialProjectIds])
        )

        // Find all classes with this curriculum
        const classesPromise = this.variableQuery(DOC_TYPES.CLASSES, {
          queries: {
            curriculumId: {
              value: values.id,
              comparator: COMPARATORS.EQUALS,
            },
          },
        })

        const [projects, classes] = await Promise.all([
          projectsPromise,
          classesPromise,
        ])

        const projectsById = keyBy(projects, "id")

        // It's possible to have no projects (or not enough or too many) on either side
        const oldProjectSchedule = getProjectScheduleBySession(
          initialProjectIds?.map((id) => projectsById[id]) ?? []
        )
        const newProjectSchedule = getProjectScheduleBySession(
          projectIds.map((id) => projectsById[id]) ?? []
        )

        // Find delta to minimize updates
        newProjectSchedule.forEach((newSession, i) => {
          // Projects can go over but sessions aren't created
          if (i >= numberOfSessions) return

          const oldSession = oldProjectSchedule[i]

          if (
            oldSession?.project?.id !== newSession?.project?.id ||
            oldSession?.fraction !== newSession?.fraction
          ) {
            sessionIndexesToUpdate.push(i)
          }
        })

        // Find all affected class sessions
        const classIds = classes.map(({ id }) => id)
        const sessionIdsToUpdate = sessionIndexesToUpdate
          .flatMap((sessionIndex) => {
            // Session id is classId-sessionIndex so no lookup needed
            return classIds.map((classId) =>
              generateSessionId(classId, sessionIndex)
            )
          })
          .sort()

        sessionIdsToUpdate.forEach((sessionId) => {
          const [, sessionIndex] = getSessionInfoFromId(sessionId)

          const updatedClassSession = {
            id: sessionId,
            projectId: newProjectSchedule[sessionIndex]?.project?.id ?? null,
            projectFraction: newProjectSchedule[sessionIndex]?.fraction ?? null,
          }
          const ref = this.getRefById<ClassSessionConstructor>(
            DOC_TYPES.CLASS_SESSIONS,
            sessionId
          )
          transaction.update(
            ref,
            Object.assign(updatedClassSession, updateMeta)
          )
        })
      }

      if (isNew) {
        const curriculumRef = this.getNewRef<CurriculumConstructor>(
          DOC_TYPES.CURRICULUMS
        )
        values.id = curriculumRef.id
        curriculumId = curriculumRef.id

        transaction.set(curriculumRef, Object.assign(values, updateMeta))
      } else {
        invariant(values.id != null, "This curriculum is missing an ID!")
        const curriculumRef = this.getRefById<CurriculumConstructor>(
          DOC_TYPES.CURRICULUMS,
          values.id
        )

        transaction.update(curriculumRef, Object.assign(values, updateMeta))
      }
    })
    return curriculumId
  }

  createOrUpdateUser = async (values: UserConstructor): Promise<void> => {
    const isNew = values.id == null
    const updateMeta = this.getUpdateMeta()
    try {
      await runTransaction(this.db, async (transaction) => {
        if (isNew) {
          const userRef = this.getNewRef<UserConstructor>(DOC_TYPES.USERS)
          values.id = userRef.id

          transaction.set(userRef, Object.assign(values, updateMeta))
        } else {
          invariant(values.id != null, "This user is missing an ID!")
          const userRef = this.getRefById<UserConstructor>(
            DOC_TYPES.USERS,
            values.id
          )

          transaction.update(userRef, Object.assign(values, updateMeta))
        }
      })
    } catch (error) {
      console.error("Add user transaction failed:", error)
      // alert(`Add user transaction failed: ${error}`)
      throw new Error(stringify(error))
    }
  }

  /**
   * SUBSCRIPTIONS
   * SUBSCRIPTIONS
   * SUBSCRIPTIONS
   * SUBSCRIPTIONS
   * SUBSCRIPTIONS
   * SUBSCRIPTIONS
   * SUBSCRIPTIONS
   * SUBSCRIPTIONS
   * SUBSCRIPTIONS
   * SUBSCRIPTIONS
   */

  createListenerForId = <T extends DataWithId>(
    docType: DOC_TYPES,
    id: string,
    callback?: (data: T | null) => void
  ): Unsubscribe => {
    if (id == null) return noop

    const ref = doc(this.db, docType, id)
    const unsubscribe = onSnapshot(
      ref,
      (docSnapshot) => {
        if (docSnapshot.exists()) {
          const data = docSnapshot.data() as T
          callback?.(data)
        } else {
          callback?.(null)
        }
      },
      (err) => {
        throw new Error(err.message)
      }
    )

    return unsubscribe
  }

  createListenerForIds = <T extends DataWithId>(
    docType: DOC_TYPES,
    ids: string[],
    callback?: (changes: FirebaseChange<T>[]) => void
  ): Unsubscribe => {
    if (ids.length === 0) return noop

    const q = query(collection(this.db, docType), where("id", "in", ids))
    const unsubscribe = onSnapshot(
      q,
      (docSnapshot) => {
        // Get changes for incremental updates
        const changes =
          docSnapshot.docChanges()?.map((change) => ({
            ...change,
            doc: change.doc.data() as T,
          })) ?? []

        callback?.(changes as FirebaseChange<T>[])
      },
      (err) => {
        throw new Error(err.message)
      }
    )

    return unsubscribe
  }

  createListenerForAllDocs = <T extends DataWithId>(
    docType: DOC_TYPES,
    callback?: (changes: FirebaseChange<T>[]) => void
  ): Unsubscribe => {
    const isDevelopment = import.meta.env.DEV

    // Development: Limit documents to reduce Firebase overhead
    let q
    if (isDevelopment && typeof DEV_FIREBASE_LIMIT !== "undefined") {
      q = query(
        collection(this.db, docType),
        where("id", "!=", null),
        limit(DEV_FIREBASE_LIMIT)
      )
    } else {
      q = query(collection(this.db, docType), where("id", "!=", null))
    }
    const unsubscribe = onSnapshot(
      q,
      (docSnapshot) => {
        const changes =
          docSnapshot.docChanges()?.map((change) => ({
            ...change,
            doc: change.doc.data() as T,
          })) ?? []

        callback?.(changes as FirebaseChange<T>[])
      },
      (err) => {
        throw new Error(err.message)
      }
    )

    return unsubscribe
  }

  createListenerForVariableQuery = <T extends DataWithId>(
    docType: DOC_TYPES,
    queryConfig: FirebaseQueryConfig,
    callback?: (changes: FirebaseChange<T>[]) => void
  ): Unsubscribe => {
    const wheres: QueryConstraint[] = []

    if (queryConfig.queries) {
      for (const key in queryConfig.queries) {
        const { value, comparator = COMPARATORS.EQUALS } =
          queryConfig.queries[key]

        if (value === undefined || (Array.isArray(value) && !value?.length)) {
          continue
        }

        if (comparator === COMPARATORS.BETWEEN) {
          if (!Array.isArray(value))
            throw new Error("BETWEEN comparator requires an array")

          const [start, end] = value as [string, string]
          wheres.push(
            where(key, COMPARATORS.GREATER_OR_EQUALS, start),
            where(key, COMPARATORS.LESS_OR_EQUALS, end)
          )
          continue
        }

        wheres.push(where(key, comparator, value))
      }
    }

    if (queryConfig.orderBy != null) {
      wheres.push(
        orderBy(queryConfig.orderBy.field, queryConfig.orderBy.direction)
      )
    }

    if (queryConfig.limitCount != null) {
      wheres.push(limit(queryConfig.limitCount))
    }

    const q = query(collection(this.db, docType), ...wheres)
    const unsubscribe = onSnapshot(
      q,
      (docSnapshot) => {
        // Get changes for incremental updates
        const changes =
          docSnapshot.docChanges()?.map((change) => ({
            ...change,
            doc: change.doc.data() as T,
          })) ?? []

        callback?.(changes as FirebaseChange<T>[])
      },
      (err) => {
        throw new Error(err.message)
      }
    )

    return unsubscribe
  }

  saveUserPin = async (userId: string, pin: string): Promise<void> => {
    const pinHash = await hashPin(pin)
    await setDoc(doc(this.db, "usedPins", pin), { pin })
    await updateDoc(
      doc(this.db, DOC_TYPES.USERS, userId),
      Object.assign({ pinHash }, this.getUpdateMeta())
    )
  }

  getUsedPins = async (): Promise<string[]> => {
    const snapshot = await getDocs(collection(this.db, "usedPins"))
    return snapshot.docs.map((doc) => doc.data().pin as string)
  }

  listenSemesterCalendar = (
    onDocs: (docs: Array<{ id: string; startDate: string | null }>) => void,
    onError: (error: Error) => void
  ): Unsubscribe => {
    return onSnapshot(
      collection(this.db, DOC_TYPES.SEMESTERS),
      (snapshot) => {
        onDocs(
          snapshot.docs.map((row) => {
            const data = row.data()
            return {
              id: typeof data.id === "string" ? data.id : row.id,
              startDate:
                typeof data.startDate === "string" ? data.startDate : null,
            }
          })
        )
      },
      (error) => onError(error)
    )
  }

  saveSemesterCalendarDocs = async (
    docs: Array<{ id: string; startDate: string }>,
    removedIds: string[] = []
  ): Promise<void> => {
    const batch = writeBatch(this.db)
    const meta = this.getUpdateMeta()
    docs.forEach((row, index) => {
      const nextStart = docs[index + 1]?.startDate
      batch.set(
        doc(this.db, DOC_TYPES.SEMESTERS, row.id),
        {
          id: row.id,
          startDate: row.startDate,
          // The end is the day before the next start. The last term stays
          // open, so a previously stored endDate has to be cleared.
          endDate: nextStart ? dayBeforeStart(nextStart) : deleteField(),
          ...meta,
        },
        { merge: true }
      )
    })
    removedIds.forEach((id) => {
      batch.delete(doc(this.db, DOC_TYPES.SEMESTERS, id))
    })
    await batch.commit()
  }

  terminateFirebase = async (): Promise<void> => {
    console.log("🔥 Terminating Firebase...")
    await terminate(this.db)

    try {
      await clearIndexedDbPersistence(this.db)
      console.log("🔥 Cleared Firestore")
    } catch (e) {
      console.error("Failed to clear Firestore persistence", e)
    }
    await deleteApp(this.app)
    console.log("🔥 Deleted Firebase app")

    // Hard reload to a clean login screen
    window.location.href = "/login"
    console.log("🔥 Hard reloading to login screen")
  }
}

// TODO: Change this to 'export const firebase = new FirebaseSingleton()'
// and then make all calls get instance on their own
export default class Firebase {
  private static instance: FirebaseSingleton | null = null

  constructor() {
    if (Firebase.instance == null) {
      Firebase.instance = new FirebaseSingleton()
    }
  }

  public static getInstance(): FirebaseSingleton {
    if (Firebase.instance == null) {
      Firebase.instance = new FirebaseSingleton()
    }
    return Firebase.instance
  }
}
